import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import CandidateView from '../pages/CandidateView';
import VoucherView from '../pages/VoucherView';
import HiringManagerView from '../pages/HiringManagerView';
import { VOUCH_REWARD } from '../brand/vouch/tokens';

const ALL_ROLES = [
  { key: 'candidate', label: 'Candidate', icon: '🎯' },
  { key: 'voucher', label: 'Voucher', icon: '🤝' },
  { key: 'hiring_manager', label: 'Hiring', icon: '💼' },
];

export default function AppLayout() {
  const { user, profile, activeRole, switchRole, addRole, signOut } = useAuth();
  const [headerStats, setHeaderStats] = useState(null);

  const handleRoleClick = (roleKey) => {
    switchRole(roleKey);
    addRole(roleKey).catch(() => {});
    setHeaderStats(null);
  };

  const renderActiveView = () => {
    switch (activeRole) {
      case 'candidate': return <CandidateView onStats={setHeaderStats} />;
      case 'voucher': return <VoucherView onStats={setHeaderStats} />;
      case 'hiring_manager': return <HiringManagerView onStats={setHeaderStats} />;
      default: return (
        <div className="max-w-lg mx-auto text-center">
          <div className="bg-white rounded-xl p-6 shadow">
            <div className="text-5xl mb-4">👋</div>
            <h2 className="text-xl font-bold text-vouch-navy mb-2">Welcome to VOUCH</h2>
            <p className="text-gray-600 text-sm">Select a role above to get started.</p>
          </div>
        </div>
      );
    }
  };

  const renderHeaderStats = () => {
    if (!profile?.linkedin_connected || !headerStats) return null;

    if (activeRole === 'candidate' && headerStats.role === 'candidate') {
      return (
        <div className="pt-2 pb-1 border-t border-white border-opacity-10">
          <p className="text-xs text-cyan-100 text-center">
            <strong className="text-white">{headerStats.jobCount} jobs</strong> in your network
            {headerStats.matchCount > 0 && (
              <span className="ml-2 bg-vouch-teal px-2 py-0.5 rounded-full text-white font-bold">
                {headerStats.matchCount} {headerStats.matchCount === 1 ? 'match' : 'matches'}
              </span>
            )}
          </p>
        </div>
      );
    }

    if (activeRole === 'voucher' && headerStats.role === 'voucher') {
      return (
        <div className="pt-2 pb-1 border-t border-white border-opacity-10">
          <p className="text-xs text-cyan-100 text-center">
            <strong className="text-white">{headerStats.potentialJobs} potential jobs</strong> • <strong className="text-white">{headerStats.matchCount} matches</strong>
            <span className="ml-1">• Earn up to <strong className="text-white">${(headerStats.matchCount * VOUCH_REWARD).toLocaleString()}</strong></span>
          </p>
          {headerStats.newRequests > 0 && (
            <p className="text-xs text-yellow-300 font-semibold text-center mt-0.5">
              {headerStats.newRequests} new {headerStats.newRequests === 1 ? 'request' : 'requests'}!
            </p>
          )}
        </div>
      );
    }

    if (activeRole === 'hiring_manager' && headerStats.role === 'hiring_manager') {
      return (
        <div className="pt-2 pb-1 border-t border-white border-opacity-10">
          <p className="text-xs text-cyan-100 text-center">
            <strong className="text-white">{headerStats.candidateCount} candidates</strong> your network knows personally
            {headerStats.jobCount > 0 && (
              <span className="ml-1">• <strong className="text-white">{headerStats.jobCount} jobs</strong> you're hiring for</span>
            )}
          </p>
          {headerStats.newCandidates > 0 && (
            <p className="text-xs text-yellow-300 font-semibold text-center mt-0.5">
              {headerStats.newCandidates} new {headerStats.newCandidates === 1 ? 'candidate' : 'candidates'}!
            </p>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      {/* Top Navigation — safe-area padding for iPhone notch */}
      <nav
        className="bg-vouch-navy shadow-lg"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        {/* Row 1: Logo + user info + sign out */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <div className="flex items-center gap-2">
            <img
              src="/brand/vouch/assets/vector/vouch-wordmark-horizontal.svg"
              alt="VOUCH"
              className="h-6 brightness-0 invert"
            />
          </div>
          <div className="flex items-center gap-2">
            {profile?.photo_url ? (
              <img
                src={profile.photo_url}
                alt=""
                className="w-7 h-7 rounded-full object-cover border-2 border-vouch-teal"
              />
            ) : (
              <div className="w-7 h-7 rounded-full bg-vouch-teal border-2 border-white flex items-center justify-center text-white text-[10px] font-bold">
                {profile?.display_name?.[0] || '?'}
              </div>
            )}
            <div className="hidden sm:block">
              <div className="text-white text-xs font-semibold leading-tight truncate max-w-[120px]">
                {profile?.display_name || user?.email}
              </div>
              {profile?.linkedin_connected && (
                <div className="text-green-300 text-[10px] leading-tight">Connected</div>
              )}
            </div>
            <button
              onClick={signOut}
              className="ml-1 px-2.5 py-1 bg-white bg-opacity-10 border border-white border-opacity-20 rounded-md text-white text-opacity-80 text-xs hover:bg-opacity-20 transition-all"
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* Row 2: Full-width role switcher */}
        <div className="px-3 pb-2">
          <div className="flex bg-white bg-opacity-10 rounded-lg p-1">
            {ALL_ROLES.map(({ key, label, icon }) => (
              <button
                key={key}
                onClick={() => handleRoleClick(key)}
                className={`flex-1 py-2 rounded-md text-sm font-medium transition-all text-center ${
                  activeRole === key
                    ? 'bg-vouch-teal text-white font-semibold shadow-sm'
                    : 'text-white text-opacity-50 hover:text-opacity-100'
                }`}
              >
                {icon} {label}
              </button>
            ))}
          </div>
        </div>

        {/* Stats subtitle bar */}
        <div className="px-4">
          {renderHeaderStats()}
        </div>
      </nav>

      {/* Main Content */}
      <main className="py-4 px-4">
        {renderActiveView()}
      </main>
    </div>
  );
}
